import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { StaffIdentity, type StaffIdentityMember } from '@/features/institute/staff/components/StaffIdentity';
import { STAFF_LEAVE_LABEL, STAFF_LEAVE_STATUS_LABEL } from '@/features/institute/staff/constants';
import { cn, getNameInitials } from '@/lib/utils';
import type { StaffLeaveStatus, StaffLeaveType } from '@/types/staffLeave';

export interface LeaveRequestItem {
  /** The application's uuid. */
  id: string;
  staffUuid: string;
  staffName: string;
  /** Empty when the record has no employee ID yet. */
  employeeId: string;
  /** The account type — Teacher, Accountant… */
  role: string;
  designation: string;
  leaveType: StaffLeaveType;
  /** Already formatted for reading — "12 Oct 2026". */
  fromDate: string;
  toDate: string;
  days: number;
  reason: string;
  status: StaffLeaveStatus;
  appliedOn: string;
  /** When it was approved or rejected, formatted. Null while nobody has decided. */
  decidedOn: string | null;
  /** Why it was rejected, when the office said. */
  decisionNote: string | null;
  /** The caller's own application — nobody decides their own leave. */
  isOwn: boolean;
  /** The staff register's row, so the first column reads as it does on the punch-ins tab. */
  member?: StaffIdentityMember;
}

export interface LeaveManagementTableProps {
  rows: LeaveRequestItem[];
  isLoading: boolean;
  /** `STAFF_LEAVE_MANAGE`: without it the decision buttons are not offered. */
  canDecide: boolean;
  /** The application a decision is being saved for, so its buttons stand down. */
  decidingId: string | null;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onApprove: (item: LeaveRequestItem) => void;
  onReject: (item: LeaveRequestItem) => void;
}

const TYPE_TONE: Record<StaffLeaveType, string> = {
  CASUAL: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  SICK: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
  PRIVILEGE: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
};

const STATUS_TONE: Record<StaffLeaveStatus, string> = {
  APPROVED: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
  PENDING: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
  REJECTED: 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20',
  CANCELLED: 'bg-sky-500/10 text-sky-600 dark:text-sky-400 border-sky-500/20',
};

export function LeaveManagementTable({
  rows,
  isLoading,
  canDecide,
  decidingId,
  page,
  pageSize,
  total,
  onPageChange,
  onApprove,
  onReject,
}: LeaveManagementTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startEntry = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endEntry = Math.min(page * pageSize, total);

  return (
    <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200/90 dark:border-slate-800 shadow-xs overflow-hidden flex flex-col">
      <div className="w-full overflow-x-auto custom-scrollbar">
        <table className="w-full text-left border-collapse min-w-[900px]">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-[#0b1322] text-[13.5px] font-bold tracking-wide text-slate-700 dark:text-slate-300">
              <th scope="col" className="sm:sticky sm:left-0 z-30 py-3.5 px-3 w-12 min-w-[48px] max-w-[48px] text-center bg-slate-100 dark:bg-[#0b1322]">#</th>
              <th scope="col" className="sm:sticky sm:left-[48px] z-30 py-3.5 px-4 min-w-[220px] bg-slate-100 dark:bg-[#0b1322] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">Staff Name</th>
              <th scope="col" className="py-3.5 px-4 min-w-[130px]">Leave Type</th>
              <th scope="col" className="py-3.5 px-4 min-w-[120px]">From Date</th>
              <th scope="col" className="py-3.5 px-4 min-w-[120px]">To Date</th>
              <th scope="col" className="py-3.5 px-4 text-center min-w-[80px]">Days</th>
              <th scope="col" className="py-3.5 px-4 min-w-[150px]">Reason</th>
              <th scope="col" className="py-3.5 px-4 text-center min-w-[110px]">Status</th>
              <th scope="col" className="py-3.5 px-4 min-w-[120px]">Applied On</th>
              <th scope="col" className="py-3.5 px-4 min-w-[120px]">Approved On</th>
              <th scope="col" className="sm:sticky sm:right-0 z-30 py-3.5 px-4 text-right min-w-[180px] bg-slate-100 dark:bg-[#0b1322] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 font-medium">
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] animate-pulse">
                  <td className="sm:sticky sm:left-0 z-20 py-3.5 px-3 w-12 min-w-[48px] max-w-[48px] bg-white dark:bg-[#111c2e]"><Skeleton className="h-4 w-5 mx-auto" /></td>
                  <td className="sm:sticky sm:left-[48px] z-20 py-3.5 px-4 min-w-[220px] bg-white dark:bg-[#111c2e] sm:border-r border-slate-200 dark:border-slate-800"><Skeleton className="h-4 w-32 mb-1.5" /><Skeleton className="h-3 w-44" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-6 w-20 rounded-full" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-20" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-20" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-8 mx-auto" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-24" /></td>
                  <td className="py-3.5 px-4 text-center"><Skeleton className="h-6 w-20 mx-auto rounded-full" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-20" /></td>
                  <td className="py-3.5 px-4"><Skeleton className="h-4 w-20" /></td>
                  <td className="sm:sticky sm:right-0 z-20 py-3.5 px-4 text-right min-w-[180px] bg-white dark:bg-[#111c2e] sm:border-l border-slate-200 dark:border-slate-800"><Skeleton className="h-8 w-36 ml-auto rounded-lg" /></td>
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={11} className="py-12 text-center">
                  <EmptyState
                    title="No leave requests found"
                    description="No leave applications match the selected criteria."
                  />
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const canDecideRow = canDecide && row.status === 'PENDING' && !row.isOwn;
                const isDeciding = decidingId === row.id;

                return (
                  <tr
                    key={row.id}
                    className="group border-b border-slate-200/80 dark:border-slate-800/80 bg-white dark:bg-[#111c2e] hover:bg-slate-50/80 dark:hover:bg-[#16253d] transition-colors"
                  >
                    {/* Index */}
                    <td className="sm:sticky sm:left-0 z-20 py-3 px-3 w-12 min-w-[48px] max-w-[48px] text-center text-sm font-bold text-slate-400 bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d]">
                      {(page - 1) * pageSize + idx + 1}
                    </td>

                    {/* The staff register's first column — the same cell as the punch-ins tab. */}
                    <td className="sm:sticky sm:left-[48px] z-20 py-2.5 px-4 min-w-[240px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-r border-slate-200 dark:border-slate-800 sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      {row.member ? (
                        <StaffIdentity
                          member={row.member}
                          badge={
                            row.isOwn && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-blue-50 dark:bg-blue-950/80 text-blue-600 dark:text-blue-400">
                                You
                              </span>
                            )
                          }
                        />
                      ) : (
                        // Only while the staff list has not arrived, or for someone
                        // no longer on it: the request alone names the person.
                        <div className="flex items-start gap-2.5">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[11px] font-black text-white shadow-2xs mt-0.5">
                            {getNameInitials(row.staffName)}
                          </span>
                          <div className="min-w-0">
                            <p className="font-extrabold text-sm text-slate-700 dark:text-white truncate">
                              {row.staffName}
                            </p>
                            <div className="flex flex-wrap flex-col items-start gap-x-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
                              {row.employeeId ? (
                                <span className="font-mono font-bold text-slate-700 dark:text-slate-300">{row.employeeId}</span>
                              ) : (
                                <span className="text-slate-400">No employee ID</span>
                              )}
                              <span className="truncate">{row.role}</span>
                              <span className="truncate">{row.designation}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>

                    {/* Leave Type */}
                    <td className="py-3 px-4">
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-bold border whitespace-nowrap',
                          TYPE_TONE[row.leaveType],
                        )}
                      >
                        {STAFF_LEAVE_LABEL[row.leaveType]}
                      </span>
                    </td>

                    {/* From Date */}
                    <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                      {row.fromDate}
                    </td>

                    {/* To Date */}
                    <td className="py-3 px-4 text-sm text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                      {row.toDate}
                    </td>

                    {/* Days */}
                    <td className="py-3 px-4 text-sm font-mono font-bold text-center text-slate-700 dark:text-slate-200">
                      {row.days}
                    </td>

                    {/* Reason */}
                    <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-400 font-medium max-w-[200px]">
                      <span className="block truncate" title={row.reason}>
                        {row.reason}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 text-center">
                      <span
                        className={cn(
                          'inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold border',
                          STATUS_TONE[row.status],
                        )}
                      >
                        {STAFF_LEAVE_STATUS_LABEL[row.status]}
                      </span>
                    </td>

                    {/* Applied On */}
                    <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap">
                      {row.appliedOn}
                    </td>

                    {/* Approved On — the day the office decided. A rejection is dated too,
                        and says so, so the column never reads as an approval it was not. */}
                    <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400 font-medium max-w-[220px]">
                      {row.decidedOn === null ? (
                        '—'
                      ) : row.status === 'REJECTED' ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="whitespace-nowrap">
                           
                            {row.decidedOn}
                          </span>
                          {row.decisionNote && (
                            <span
                              className="block truncate text-xs text-rose-600 dark:text-rose-400 font-medium"
                              title={row.decisionNote}
                            >
                              <span className="text-rose-600 dark:text-rose-400 font-semibold">Rejected</span>{' '} : {row.decisionNote}
                            </span>
                          )}
                        </div>
                      ) : (
                        row.decidedOn
                      )}
                    </td>

                    {/* Decision — on the row itself rather than behind a menu */}
                    <td className="sm:sticky sm:right-0 z-20 py-3 px-4 text-right min-w-[180px] bg-white dark:bg-[#111c2e] group-hover:bg-slate-50 dark:group-hover:bg-[#16253d] sm:border-l border-slate-200 dark:border-slate-800 sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.06)] dark:sm:shadow-[-4px_0_8px_-3px_rgba(0,0,0,0.3)]">
                      {canDecideRow ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            disabled={isDeciding}
                            onClick={() => onApprove(row)}
                            aria-label={`Approve ${row.staffName}'s leave`}
                            className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-2.5 py-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                          >
                            <Check className="h-3.5 w-3.5" aria-hidden />
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={isDeciding}
                            onClick={() => onReject(row)}
                            aria-label={`Reject ${row.staffName}'s leave`}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 dark:border-rose-900/60 bg-rose-50 dark:bg-rose-950/40 px-2.5 py-1.5 text-xs font-bold text-rose-700 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500"
                          >
                            <X className="h-3.5 w-3.5" aria-hidden />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">
                          {row.isOwn && row.status === 'PENDING' ? 'Your request' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer & Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 border-t border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-[#0b1322]/50 text-xs text-slate-500 dark:text-slate-400">
        <div>
          Showing <span className="font-bold text-slate-700 dark:text-slate-200">{startEntry}</span> to{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{endEntry}</span> of{' '}
          <span className="font-bold text-slate-700 dark:text-slate-200">{total}</span> entries
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {Array.from({ length: Math.min(totalPages, 5) }).map((_, i) => {
            const pageNumber = i + 1;
            const isActive = page === pageNumber;
            return (
              <button
                key={pageNumber}
                type="button"
                onClick={() => onPageChange(pageNumber)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all',
                  isActive
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {pageNumber}
              </button>
            );
          })}

          {totalPages > 5 && (
            <>
              <span className="px-1 text-slate-400">...</span>
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className={cn(
                  'h-8 w-8 rounded-lg text-xs font-bold transition-all',
                  page === totalPages
                    ? 'bg-emerald-500 text-white shadow-xs'
                    : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300',
                )}
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
